import React from 'react'
import {FormattedMessage} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {getScrollTargetSpecificRoom} from 'hooks/useHandleScrollToClickedOfferTarget/utils'
import {isRoomsViewExpanded} from 'modules/accommodation/selectors'
import {collapseRoomsView, expandRoomsView} from 'modules/accommodation/slice'
import {getFilteredRoomsData} from 'modules/searchApi/selectors'

import {Button} from '@daedalus/atlas/Button'
import {useDeviceLayout} from '@daedalus/atlas/context/deviceLayout'
import ContentWrapper from '@daedalus/atlas/helpers/ContentWrapper'
import {Flex} from '@daedalus/atlas/helpers/Flex'
import {Icon} from '@daedalus/atlas/Icon'
import {SkeletonLoader} from '@daedalus/atlas/SkeletonLoader'
import {Text} from '@daedalus/atlas/Text'
import {trackEvent} from '@daedalus/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Team
} from '@daedalus/core/src/analytics/types/Events'

import {COMPACT_ROOMS_AMOUNT} from '../constants'

interface Props {
  isLoading: boolean
}

export const RoomsSectionFooter = ({isLoading}: Props) => {
  const dispatch = useDispatch()
  const {splitBooking, rooms, totalResults} = useSelector(getFilteredRoomsData)
  const isListExpanded = useSelector(isRoomsViewExpanded)
  const {isMobile, isDesktopXs} = useDeviceLayout()
  const isMobileOrTablet = isMobile || isDesktopXs
  const filteredRoomsLength = rooms.length + (splitBooking ? 1 : 0)

  const handleCollapseAndScroll = () => {
    dispatch(collapseRoomsView())

    // Access to the last unexpanded room's ID
    const lastVisibleRoomId = rooms[COMPACT_ROOMS_AMOUNT - 1].id

    const element = document.getElementById(
      getScrollTargetSpecificRoom(lastVisibleRoomId)
    )

    setTimeout(() => {
      element?.scrollIntoView({behavior: 'smooth'})
    })
  }
  const toggleExpandRoomsList = () => {
    if (!isListExpanded) {
      dispatch(
        trackEvent({
          category: Category.User,
          entity: Entity.SeeMoreRoomsButton,
          action: Action.Clicked,
          team: Team.Select
        })
      )

      dispatch(expandRoomsView())
    } else {
      handleCollapseAndScroll()
    }
  }
  if (isLoading && !isMobileOrTablet)
    return (
      <ContentWrapper paddingTop="s300">
        <Flex.Row justifyContent="center">
          <SkeletonLoader width={365} height={50} />
        </Flex.Row>
      </ContentWrapper>
    )
  if (!totalResults) return null

  return (
    <ContentWrapper paddingTop="s300">
      <Flex.Row justifyContent="center">
        {filteredRoomsLength > COMPACT_ROOMS_AMOUNT ? (
          <Button
            variant="secondary"
            onClick={toggleExpandRoomsList}
            iconEnd={
              <Icon name={isListExpanded ? 'ChevronUp' : 'ChevronDown'} />
            }
            dataId="roomSectionFooterButton"
          >
            {isListExpanded ? (
              <FormattedMessage
                id="roomsList.collapseRoomsList"
                defaultMessage="Hide room options"
              />
            ) : (
              <FormattedMessage
                id="roomsList.expandRoomsList"
                defaultMessage="Show more rooms"
              />
            )}
          </Button>
        ) : (
          <Text align="center" variant="bodyS" colorPath="content.neutral.c600">
            <FormattedMessage
              id="noMoreRoomsToShowLabel"
              defaultMessage="No more rooms to show"
            />
          </Text>
        )}
      </Flex.Row>
    </ContentWrapper>
  )
}
